import { forwardRef } from "react";

import { Box, IconButton } from "@mui/material";
import { DateTimePicker as MUIDateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { gray } from "configs/colors";
import { shadows } from "configs/shadows";
import { rem } from "utils";

import { useDateTimePicker } from "./DateTimePicker.hooks";
import {
  DateTimePickerProps,
  DateTimePickerReference,
  DateTimePickerTextFieldProps,
} from "./DateTimePicker.types";
import { CalendarIcon } from "../Icon";
import TextField from "../TextField/TextField";

const DateTimePickerTextField = ({
  InputProps,
  onClick,
  onChange,
  ...innerProps
}: DateTimePickerTextFieldProps) => {
  return (
    <TextField
      {...innerProps}
      onChangeEvent={onChange}
      onClick={onClick}
      InputProps={{
        ...(InputProps ?? {}),
        startAdornment: (
          <IconButton
            size="small"
            disabled={innerProps.disabled}
            sx={{ marginX: rem(-6) }}
            onClick={
              onClick as unknown as React.MouseEventHandler<HTMLButtonElement>
            }
          >
            <CalendarIcon fontSize="small" sx={{ color: gray[700] }} />
          </IconButton>
        ),
        endAdornment: null, // Hide default "Calendar" icon of MUI Date Picker
        readOnly: true,
      }}
    />
  );
};

const DateTimePicker = forwardRef<DateTimePickerReference, DateTimePickerProps>(
  (
    {
      defaultValue,
      value,
      placeholder,
      required,
      helperText,
      error,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const {
      rootRef,
      valueState,
      openCalendar,
      onChangeHandler,
      onCalendarCloseHandler,
      toggleCalendar,
    } = useDateTimePicker({
      ref,
      defaultValue,
      value,
      onChange,
    });

    return (
      <Box ref={rootRef}>
        <MUIDateTimePicker
          value={valueState}
          open={openCalendar}
          onChange={onChangeHandler}
          onClose={onCalendarCloseHandler}
          slots={{ textField: DateTimePickerTextField }}
          slotProps={{
            popper: {
              sx: {
                ".MuiPickersPopper-paper": {
                  boxShadow: shadows.lg,
                },
              },
            },
            textField: {
              required,
              placeholder,
              helperText,
              error,
              onClick: toggleCalendar as unknown as never,
            },
          }}
          {...innerProps}
        />
      </Box>
    );
  }
);

export default DateTimePicker;
